import DropdownSelector from "@/components/dropdown-selector/DropdownSelector";
import { FC, useEffect, useMemo, useState } from "react";
import { useSelector } from 'react-redux';
import useUserCapability from "../hooks/useUserCapability";
import { ROLES } from "../constants/preset-constants";
import { USER_CAPABILITIES_SYNC } from "../constants/user-capabilities-constants";
import { toast } from "react-toastify";
import { DialogButton, DialogContent } from "../components/dialog/Dialog";
import { useDialog } from "../components/dialog/DialogContext";
import ReplaceMasterControllerModal from "../components/hardware/install/swapMasterController/ReplaceMasterControllerModal";
import TableTab from "../components/tabs/table-tab";
import { COMMAND_STATE, FILTER_COMMAND, HEART_BEAT_COMMAND, MESSAGE_OVERLOAD_ERROR, MONITORING_COMMAND, MONITORING_COMMAND_STATE } from "../constants/command-status";
import Commands from "../constants/commands";
import { DEVICE_STATES2 } from "../constants/device-status";
import { isCloud } from "../constants/license-constants";
import { HARDWARE_CONTROLLERS } from "../constants/menu-types";
import { EngineSubCmds, HardwareSubCmds } from "../constants/sub-commands";
import { SYNC_ALL, SYNC_NON_BIO_ONLY, SYNC_SELECTED, SYSNC_BIO_ONLY } from "../constants/synce-devices-types";
import { DEVICE_TYPE_CATEGORY, TYPE_PER_DEVICE_TYPE, getCountsCategory } from "../constants/system-constants";
import { useLocale } from "../contexts/localeContext";
import { TableContextProvider } from "../contexts/table/TableContextProvider";
import { TranslationKeys } from "../contexts/TranslationKeys";
import { useIcaWebsocketContext } from "../contexts/websocket/IcaWebsocketContext";
import { ButtonTypes } from "../models/ButtonTypes";
import { Category } from "../models/Category";
import columns from '../models/ColumnDefinitions/ControllerColumns';
import { Controller } from "../models/Controller";
import { ExecuteCommand } from "../models/ExecuteCommand";
import { Lookup } from "../models/Lookup";
import LookupTypes from "../models/LookupTypes";
import searchColumns from '../models/search/ControllerSearch';
import SiteSearch from "../models/search/SiteSearch";
import { DeleteEntityResult } from "../models/services/DeleteEntityResult";
import { getDeviceTableLegendItems } from "../models/TableLegend";
import { useGlobalState } from "../Providers";
import { getControllerById, getControllers } from "../services/controllerService";
import { executeCommand, executeCommandForDevice } from "../services/executeCommandService";
import { getSiteNamesWithPagination } from "../services/siteService";
import { getSystemStats } from "../services/systemService";
import { getErrorMessage, showError } from "../services/utilities/ServiceError";
import { getDeviceStatusWithLicense } from "../services/utilities/StatusBuilder";
import { IAppState } from "../store/types";
import { getCommand, getDeviceStates, getWsDevice } from "../utilities/WebsocketUtil";
import SystemStat from "@/models/SystemStat";

interface Props {
    setShowInstallMasterControllerModal: any
    setBusy: (isBusy: boolean) => void,
    categories: Category[],
    setCategories: (category: Category[]) => void,
    setBusyMessage: (message: string) => void,
}

const MasterControllerTab: FC<Props> = ({ setBusy, categories, setCategories, setShowInstallMasterControllerModal, setBusyMessage }) => {
    const { translate } = useLocale();
    const canSync = useUserCapability(USER_CAPABILITIES_SYNC);
    const { buildInfo } = useGlobalState() ?? {};
    const title = translate.byKey(TranslationKeys.masterControllers);
    const lookup: Lookup[] = [
        {
            name: LookupTypes.site,
            values: [],
            dataMethod: getSiteNamesWithPagination,
            searchAccessor: SiteSearch.find(item => item.accessor === "name")?.searchOnName
        }
    ];
    const [selectedIds, setSelectedIds] = useState<string[]>([])
    const [selectedRows, setSelectedRows] = useState<any[]>([])
    const itemPath = '/access-control/hardware/master-controller/';
    const user = useSelector((state: IAppState) => (state.userInfo));
    const isSystemOwner = Number(user?.profileId) === ROLES.ID_SYSTEM_OWNER || Number(user?.inheritsFrom) === ROLES.ID_SYSTEM_OWNER;
    const [updateTable, setUpdateTable] = useState(() => (updateMethod: (tableData: any[]) => any[]) => { return });
    const [deviceStatusInfo, setDeviceStatusInfo] = useState<{ ipAddress: string, statusCode: number }>({
        ipAddress: "",
        statusCode: 0,
    })
    const [deviceQueInfo, setDeviceQueInfo] = useState<{ fixedAddress: string, queue: number }>({
        fixedAddress: "",
        queue: 0
    })

    const syncDeviceOptions = [
        { value: SYNC_ALL, label: translate.byKey(TranslationKeys.syncAll) },
        { value: SYSNC_BIO_ONLY, label: translate.byKey(TranslationKeys.syncBiometricOnly) },
        { value: SYNC_NON_BIO_ONLY, label: translate.byKey(TranslationKeys.syncNonBioOnly) },
        { value: SYNC_SELECTED, label: translate.byKey(TranslationKeys.syncSelectedOnly) }
    ];

    const getSyncOptions = () => {
        if (!isSystemOwner) {
            return syncDeviceOptions.filter(option => option.value === SYNC_SELECTED);
        }

        // Cloud does not yet support biometrics. Filter out the option.
        if (isCloudMode) {
            return syncDeviceOptions.filter(option => option.value !== SYSNC_BIO_ONLY);
        }
        return syncDeviceOptions;
    };

    const isCloudMode = useMemo(() => {
        return isCloud(buildInfo?.usage);
    }, [buildInfo?.usage]);

    // The device status codes this view is interesting in
    const filterStatusCodes = [
        DEVICE_STATES2.QUEUE_STATE,
        DEVICE_STATES2.OFFLINE,
        DEVICE_STATES2.ONLINE_ICMP,
        DEVICE_STATES2.ONLINE_APP,
        DEVICE_STATES2.BUSY,
        DEVICE_STATES2.UPLOADING];

    const [showSwapControllerModal, setShowSwapMasterControllerModal] = useState<boolean>(false);
    const [controllerToReplace, setControllerToReplace] = useState<Controller>({
        id: 0,
        name: ''
    });
    const icaWebSocket = useIcaWebsocketContext();
    let heartbeatInterval: any;
    let filterDiagnosticsRequestTimeOut: any;
    let filterStatusCodesRequestTimeOut: any;
    const dialog = useDialog();

    useEffect(() => {
        updateTable((tableData) => {
            return updateControllerState(deviceStatusInfo.ipAddress, deviceStatusInfo.statusCode, tableData)
        })
    }, [deviceStatusInfo])

    useEffect(() => {
        updateTable((tableData) => {
            return updateControllerQueue(deviceQueInfo.fixedAddress, deviceQueInfo.queue, tableData)
        })
    }, [deviceQueInfo])

    const tableDataUpdate = (updateTableMethod: (updateMethod: (tableData: any[]) => any[]) => void) => {
        setUpdateTable(() => updateTableMethod);
    }

    const onDelete = async (id: string): Promise<DeleteEntityResult> => {
        setBusy(true);
        setBusyMessage(translate.byKey(TranslationKeys.unstallingMessage))

        let fixedAddress: string | undefined;

        try {
            const controller = await getControllerById(id);
            fixedAddress = controller.fixedAddress
        } catch (err) {
            toast.error(translate.byKey(TranslationKeys.deviceNotFound));
        }

        const command: ExecuteCommand = {
            cmd: Commands.Engine.label,
            cmdSubType: HardwareSubCmds.Uninstall_Controller,
            userLoginId: user.id,
            fixedAddress: fixedAddress,
            jsonResponse: ""
        }
        return new Promise((resolve, reject) => {
            executeCommandForDevice(command)
                .then(
                    async (deleteResult: any) => {
                        resolve(deleteResult);
                        const showTabCounts = window.sessionStorage.getItem("showTabCounts") === "1";
                        const systemStats: SystemStat | undefined = showTabCounts ? await getSystemStats(TYPE_PER_DEVICE_TYPE) : undefined;

                        const newCategories: Category[] = categories.map((category) => {
                            if (category.id === 0) {
                                return { ...category, count: (category?.count ?? 1) - 1 }
                            }
                            else if (category.id === 2) {
                                return { ...category, count: systemStats ? getCountsCategory(systemStats, DEVICE_TYPE_CATEGORY.READERS) : 0 }
                            }
                            else if (category.id === 3) {
                                return { ...category, count: systemStats ? getCountsCategory(systemStats, DEVICE_TYPE_CATEGORY.IO_MODULES) : 0 }
                            }
                            else if (category.id === 4) {
                                return { ...category, count: systemStats ? getCountsCategory(systemStats, DEVICE_TYPE_CATEGORY.LOCKS) : 0 }
                            }

                            return category;
                        });

                        setCategories(newCategories);
                        setBusy(false);
                    },
                    deleteResult => {
                        reject(deleteResult);
                        setBusy(false);
                    })
        });
    }

    useEffect(() => {
        if (icaWebSocket.connectionState) {
            sendFilterDiagnosticsRequest();
            sendfilterStatusCodeRequest();
            sendHeartbeatRequest();
            icaWebSocket.startStopTopic(MONITORING_COMMAND.MONITORING_DEVICE, MONITORING_COMMAND.MONITORING, MONITORING_COMMAND_STATE.MONITORING_START);
            icaWebSocket.setOnMessage(onMessage)
        }

        return () => {
            clearInterval(heartbeatInterval);
            clearTimeout(filterDiagnosticsRequestTimeOut);
            clearTimeout(filterStatusCodesRequestTimeOut);
            icaWebSocket.startStopTopic(MONITORING_COMMAND.MONITORING_DEVICE, MONITORING_COMMAND.MONITORING, MONITORING_COMMAND_STATE.MONITORING_STOP);
        }
    }, [icaWebSocket.connectionState]);

    const sendHeartbeatRequest = () => {
        const heartbeatRequest = {
            "command": MONITORING_COMMAND.MONITORING_DEVICE,
            "attributes": {
                "name": HEART_BEAT_COMMAND.HEART_BEAT,
            }
        }

        heartbeatInterval = setInterval(() => {
            icaWebSocket.sendMessage(JSON.stringify(heartbeatRequest));
        }, 20000);
    }

    const sendFilterDiagnosticsRequest = () => {
        // Tell the server which network updates to report on
        const filterDiagnosticsRequest = {
            "command": MONITORING_COMMAND.MONITORING_DEVICE,
            "attributes": {
                "name": FILTER_COMMAND.FILTER,
                "diagnosticsFilters": {
                    "siteIdFilters": "",
                    "includeControllers": true,
                    "includeDoorControllers": false,
                    "includeBiometrics": false,
                    "includeOfflineDevices": true,
                    "includeOnlineDevices": true
                }
            }
        }

        filterDiagnosticsRequestTimeOut = setTimeout(() => {
            icaWebSocket.sendMessage(JSON.stringify(filterDiagnosticsRequest))
        }, 5000)
    }

    const sendfilterStatusCodeRequest = () => {
        // Tell the server to only report on these device status codes.
        const filterStatusCodesRequest = {
            "command": MONITORING_COMMAND.MONITORING_DEVICE,
            "attributes": {
                "name": FILTER_COMMAND.FILTER,
                "statusCodeFilters": {
                    "includeStatusCodes": filterStatusCodes
                }
            }
        }

        filterStatusCodesRequestTimeOut = setTimeout(() => {
            icaWebSocket.sendMessage(JSON.stringify(filterStatusCodesRequest))
        }, 5000)
    }

    const onMessage = (data) => {
        if (!data.data.includes(MESSAGE_OVERLOAD_ERROR)) {
            const response = JSON.parse(data.data);
            if (getCommand(response) === MONITORING_COMMAND.MONITORING_DEVICE) {
                const deviceStates = getDeviceStates(response);
                if (Array.isArray(deviceStates)) {
                    deviceStates.forEach((deviceState) => {
                        const wsDevice = getWsDevice(deviceState);

                        if (wsDevice) {
                            // Queue count
                            if (wsDevice.fixedAddress) {
                                const queueCount = wsDevice.uploaderQueueCount;
                                setDeviceQueInfo({
                                    fixedAddress: wsDevice.fixedAddress,
                                    queue: queueCount
                                });
                            }

                            // State updates (online / offline / busy / uploading)
                            const codeConnected = wsDevice.codeConnected;
                            const codeActivity = wsDevice.codeActivity;
                            const statusCode = wsDevice.statusCode;

                            // Online, offline, and unknown states
                            if (wsDevice.ipAddress && codeConnected && (codeConnected === DEVICE_STATES2.ONLINE_APP
                                || codeConnected === DEVICE_STATES2.ONLINE_ICMP
                                || codeConnected === DEVICE_STATES2.OFFLINE
                                || codeConnected === DEVICE_STATES2.UNKNOWN)) {
                                setDeviceStatusInfo({
                                    ipAddress: wsDevice.ipAddress,
                                    statusCode: codeConnected
                                });
                            }

                            // Busy and uploading states
                            if (wsDevice.ipAddress && codeActivity && (codeActivity === DEVICE_STATES2.UPLOADING || codeActivity === DEVICE_STATES2.BUSY)) {
                                setDeviceStatusInfo({
                                    ipAddress: wsDevice.ipAddress,
                                    statusCode: codeActivity
                                });
                            }

                            // Unlicensed
                            if (wsDevice.ipAddress && statusCode && statusCode === DEVICE_STATES2.UNLICENSED) {
                                setDeviceStatusInfo({
                                    ipAddress: wsDevice.ipAddress,
                                    statusCode: statusCode
                                });
                            }
                        }
                    });
                }
            }
        }
    }

    const updateControllerState = (ipAddress: string, statusCode: number, controllers: Controller[]) => {
        const controller = controllers.find((controller) => controller.ipAddress === ipAddress);
        if (controller) {
            const status = getDeviceStatusWithLicense(true, controller.enabled, statusCode);
            if (controller.ipAddress === ipAddress && controller.enabled && controller.status !== status) {
                const indexOf = controllers.indexOf(controller);
                controllers[indexOf].status = status;
            }
        }

        return controllers;
    }

    const updateControllerQueue = (fixedAddress: string, queue: number, controllers: Controller[]) => {
        const controller = controllers.find((controller) => controller.fixedAddress === fixedAddress);
        if (controller && controller.queue !== queue) {
            const indexOf = controllers.indexOf(controller);
            controllers[indexOf].queue = queue;
        }

        return controllers;
    }

    // user preference items
    const userLoginId = useSelector((state: any) => (state.userInfo.id))

    const initialiseUserPreferences = (setDetails: (userLoginId, HARDWARE_CONTROLLERS) => void) => {
        setDetails(userLoginId, HARDWARE_CONTROLLERS);
    }
    // <-- end context

    const onControllerSwap = (item: Controller) => {
        setControllerToReplace(item);
        setShowSwapMasterControllerModal(true);
    }

    const postReplace = (status: string) => {
        if (status === COMMAND_STATE.COMMAND_COMPLETE) {
            setCategories([...categories])
            toast.success(translate.byKey(TranslationKeys.successfullyReplacedController));
        }
    }

    const checkEngineStatus = async () => {
        try {
            const command: ExecuteCommand = {
                cmd: Commands.Engine.label,
                cmdSubType: EngineSubCmds.Is_Engine_Started,
                userLoginId: user.id,
                jsonResponse: ""
            }

            const response: any = await executeCommand(command);
            return response.status === COMMAND_STATE.ENGINE_STOPPED ? true : false;
        } catch (error) {
            toast.warn(getErrorMessage(error));
        }
    }

    const checkSyncOption = async (syncOption: string) => {
        if (!isSystemOwner && syncOption !== SYNC_SELECTED) {
            return;
        }

        const deviceSyncStatus = await checkEngineStatus();
        const dialogButtons: DialogButton[] = [
            {
                id: "btn_sync",
                onClick: () => syncController(syncOption),
                text: translate.byKey(TranslationKeys.syncDevices),
                type: ButtonTypes.BlueRoundedButton
            },
            {
                id: "btn_Cancel",
                onClick: () => { }, // Empty onClick will just close the dialog
                text: translate.byKey(TranslationKeys.cancel),
                type: ButtonTypes.TextButton,
                className: "justify-center px-4 py-1 text-base font-medium sm:ml-3 sm:w-auto sm:text-sm"
            }
        ];

        if (syncOption === SYNC_SELECTED && selectedIds.length === 0) {
            toast.warn(translate.byKey(TranslationKeys.pleaseSelectMasterControllerDevice));
        }
        else if (syncOption === SYNC_ALL) {
            const toastMessage = deviceSyncStatus === true ? translate.byKey(TranslationKeys.syncAllDevicesMessage) + '\n' + translate.byKey(TranslationKeys.engineSelectedDevicesOnly) : translate.byKey(TranslationKeys.syncAllDevicesMessage);
            const dialogContent: DialogContent = {
                title: translate.byKey(TranslationKeys.syncAllDevices),
                message: toastMessage,
                buttons: dialogButtons
            }
            dialog.pushInfo(dialogContent);
        }
        else if (syncOption === SYNC_SELECTED && selectedIds.length > 0 && deviceSyncStatus === true) {
            const dialogContent: DialogContent = {
                title: translate.byKey(TranslationKeys.syncSelectedDevicesOnly),
                message: translate.byKey(TranslationKeys.engineSelectedDevicesOnly),
                buttons: dialogButtons
            }
            dialog.pushInfo(dialogContent);
        }
        else {
            syncController(syncOption);
        }
    }

    const syncController = async (syncOption: string) => {
        try {
            const fixedAddresses = selectedRows.map((row) => row.fixedAddress);
            const command: ExecuteCommand = {
                cmd: Commands.Engine.label,
                cmdSubType: HardwareSubCmds.Sync_Controllers,
                userLoginId: user.id,
                jsonResponse: "",
                syncType: syncOption,
                addresses: syncOption === SYNC_ALL || syncOption === SYNC_SELECTED ? fixedAddresses : []
            }
            executeCommand(command)
                .then(() => {
                    toast.success(translate.byKey(TranslationKeys.syncInProgress));
                })
                .catch((error) => {
                    dialog.pushError({ title: translate.byKey(TranslationKeys.error), message: getErrorMessage(error) })
                })
        } catch (error) {
            showError(error, toast.warn);
        }
    }

    return <>
        <TableContextProvider
            initialiser={(setDetails) => initialiseUserPreferences(setDetails)}
            dataMethod={getControllers}
            tableDescriptor={columns}
            searchDescriptor={searchColumns}
            legendItems={getDeviceTableLegendItems(translate)}
            useTable
            usePagination
            useFilter
            useSearch
            useLegend
            useSort
            deleteMethod={onDelete}
            tableDataUpdate={tableDataUpdate}
            swapMethod={onControllerSwap}
            lookup={lookup}
            onAddNew={() => setShowInstallMasterControllerModal(true)}>
            <TableTab
                selectedIds={selectedIds}
                setSelectedIds={setSelectedIds}
                selectedRows={selectedRows}
                setSelectedRows={setSelectedRows}
                title={title}
                selectRows={true}
                rowPath={itemPath}
                cyName="masterController">
                {canSync && <DropdownSelector className="flex px-4 rounded-full shadow text-white bg-sidemenu-focus text-sm items-center" buttonClassName="flex px-8 items-center"
                    onSelected={(event) => checkSyncOption(event.value)}
                    options={getSyncOptions()}>
                    {translate.byKey(TranslationKeys.commands)}
                </DropdownSelector>}
            </TableTab>
        </TableContextProvider>
        <ReplaceMasterControllerModal
            setBusyMessage={setBusyMessage}
            controllerToReplace={controllerToReplace}
            isOpen={showSwapControllerModal}
            onClose={() => setShowSwapMasterControllerModal(false)}
            postReplace={postReplace}
            setBusy={setBusy} />
    </>
};

export default MasterControllerTab;
